<template>
  <el-card
    class="nav-card"
    shadow="never"
    :body-style="{ padding: 0, display: 'flex', 'flex-direction': 'column', height: '100%' }"
  >
    <div class="logo" @click="goHome">
      <span class="logo-text">Label System</span>
    </div>
    <div class="menu">
      <el-skeleton :loading="loading" animated>
        <template #template>
          <div style="padding: 14px" v-for="item in 10" :key="item">
            <el-skeleton-item variant="h3" style="width: 50%" />
            <el-skeleton-item variant="text" />
          </div>
        </template>
        <template #default>
          <el-menu
            :collapse="isCollapse"
            :collapse-transition="false"
            :default-active="defaultActive"
            @select="selectChange"
          >
            <template v-for="item in menuList" :key="item.menuId">
              <MenuItem :menuItem="item" :isCollapse="isCollapse" />
            </template>
          </el-menu>
        </template>
      </el-skeleton>
    </div>
  </el-card>
</template>

<script>
import { defineComponent, ref, watch, computed, provide } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import MenuItem from './MenuItem.vue'
import { $t } from '@/lang'
import { ElMessage } from 'element-plus'
import { SUBMENU_PREFIX } from './constants'
import dayjs from 'dayjs'

export default defineComponent({
  name: 'ContainerNavMenu',
  components: {
    MenuItem
  },
  props: {
    collapseStatus: Boolean
  },
  setup(props) {
    const isCollapse = ref(false)
    const route = useRoute()
    const router = useRouter()
    const store = useStore()
    const loading = computed(() => store.state.menu.menuLoading)

    /**
     * 菜单层级
     */
    const formatLevel = (arr, level, childKey) => {
      arr.forEach(el => {
        el.level = level
        el.isNew = el.createDate
          ? dayjs(el.createDate).isAfter(dayjs().subtract(1, 'month'))
          : false
        if (el[childKey]?.length) {
          formatLevel(el[childKey], level + 1, childKey)
        }
      })
      return arr
    }

    const menuList = computed(() => formatLevel(store.state.menu.menuList, 1, 'childMenuList'))

    const currentMenu = ref('')
    const defaultActive = ref('')

    const setDefaultMenu = path => {
      const item = findMenuItem(path, menuList.value)
      if (!item.target) return
      if ((item.target.level ?? 0) <= 2) {
        defaultActive.value = path
      } else {
        defaultActive.value = `${SUBMENU_PREFIX}-${item.parents[1]?.menuId}`
      }
    }

    const findMenuItem = (path, menuList, parents = []) => {
      for (let i = 0; i < menuList.length; i++) {
        const item = menuList[i]
        if (item.menuRouteUrl === path) {
          return {
            target: item,
            parents,
            path
          }
        }
        if (item.childMenuList) {
          const child = findMenuItem(path, item.childMenuList, [...parents, item])
          if (child.target) {
            return {
              target: child.target,
              parents: child.parents,
              path
            }
          }
        }
      }
      return {
        target: null,
        parents,
        path
      }
    }

    watch(
      () => route.path,
      newPath => setDefaultMenu(newPath),
      { immediate: true } // 立即执行一次
    )

    const goHome = () => {
      router.push('/')
      store.commit('menu/setCurrentTab', {
        name: $t('main_home_home_name'),
        routeUrl: '/'
      })
      // history.pushState(null, null, '/')
    }

    watch(
      () => props.collapseStatus,
      newStatus => {
        isCollapse.value = newStatus
      }
    )

    const selectChange = url => {
      const isSubMenu = url.startsWith(SUBMENU_PREFIX)
      if (isSubMenu) return
      // 控制塔大屏域名和token编码拼接到url后面
      if (url.indexOf('logistics-control') > -1) {
        const { token } = store.state.user.userInfo
        const envInfo = store.state.env.envInfo || {}
        if (!envInfo?.EnvironmentName || !envInfo?.RegionName) {
          return ElMessage.error('环境异常，请重新登陆！')
        }
        const baseUrl = `/lct/${envInfo?.EnvironmentName?.toLowerCase()}-${envInfo?.RegionName?.toLowerCase()}/gw/omd/lct-management/basicData/operatingList`
        const env = `${location.host}/login`
        const lctUrl = `${url}?token=${encodeURIComponent(token)}&baseUrl=${encodeURIComponent(
          baseUrl
        )}&env=${encodeURIComponent(env)}`
        window.open(lctUrl)
      } else {
        router.push(url)
      }
      // history.pushState(null, null, index)
    }

    provide('selectChange', selectChange)

    return {
      isCollapse,
      menuList,
      store,
      currentMenu,
      defaultActive,
      goHome,
      selectChange,
      loading
    }
  }
})
</script>

<style lang="scss" scoped>
.nav-card {
  height: 100%;
  border: none !important;
  border-radius: 0;

  .logo {
    height: 64px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;

    .logo-text {
      font-size: 20px;
      font-weight: bold;
      color: $primary-color;
      letter-spacing: 1px;
      white-space: nowrap;
    }
  }

  .menu {
    margin-top: 10px;
    flex: 1;
    overflow: hidden;
    overflow-y: auto;
    padding-bottom: 40px;
    box-sizing: border-box;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  :deep(.el-menu) {
    border: none;
    width: 100%;
    .el-sub-menu__title,
    .level-1-item {
      height: 44px;
      line-height: 44px;
      .el-sub-menu__icon-arrow {
        transform: rotateZ(-90deg);
      }

      &:hover {
        background-color: #f4f7ff;
        color: #1d4cd2;
        .el-sub-menu__icon-arrow {
          color: #1d4cd2;
        }
      }
    }

    .el-sub-menu {
      &.is-opened > .el-sub-menu__title {
        .el-sub-menu__icon-arrow {
          transform: rotateZ(0);
        }
      }
      &:hover {
        & > .el-sub-menu__title {
          background-color: #f4f7ff;
          color: #1d4cd2;
          .el-sub-menu__icon-arrow {
            color: #1d4cd2;
          }
        }
      }
    }

    :deep(.el-menu-item) {
      i {
        color: $title-color;
      }
    }
  }
}
</style>
